/**
 * Derive buyer ↔ seller hostnames for Vaiu deployments.
 *
 * Rules (buyer → merchant):
 * - Hostname has exactly 2 labels (e.g. vaiu.app, atomicmarketplace.com) → merchant is sell.<buyer>
 * - Hostname has ≥3 labels and the first label is "app" (e.g. app.paywithvaiu.com) → merchant is sell.<rest>
 *
 * Merchant → buyer is the inverse, with explicit exceptions where the buyer is not app.* (e.g. Atomic apex).
 */

/** Where buyer is not the 2-label apex paired to this seller (see buyerHostnameToMerchantHostname). */
const MERCHANT_TO_BUYER_SPECIAL: Record<string, string> = {
  'sell.atomicmarketplace.com': 'atomicmarketplace.com',
  'sell.paywithvaiu.com': 'app.paywithvaiu.com',
};

/**
 * Map buyer hostname → merchant (seller) hostname.
 */
export function buyerHostnameToMerchantHostname(buyerHost: string): string {
  const h = buyerHost.toLowerCase().trim();
  const parts = h.split('.').filter(Boolean);
  if (parts.length === 2) {
    return `sell.${h}`;
  }
  if (parts.length >= 3 && parts[0] === 'app') {
    return `sell.${parts.slice(1).join('.')}`;
  }
  return h;
}

/**
 * Map merchant hostname → buyer hostname.
 */
export function merchantHostnameToBuyerHostname(merchantHost: string): string {
  const m = merchantHost.toLowerCase().trim();
  if (MERCHANT_TO_BUYER_SPECIAL[m]) {
    return MERCHANT_TO_BUYER_SPECIAL[m];
  }
  if (!m.startsWith('sell.')) {
    return m;
  }
  const rest = m.slice('sell.'.length);
  const apexCandidate = rest;
  const appCandidate = `app.${rest}`;
  if (buyerHostnameToMerchantHostname(apexCandidate) === m) {
    return apexCandidate;
  }
  if (buyerHostnameToMerchantHostname(appCandidate) === m) {
    return appCandidate;
  }
  return apexCandidate;
}

/**
 * https:// buyer origin for links from the merchant app.
 */
export function buyerOriginFromMerchantHostname(merchantHost: string): string {
  const host = merchantHostnameToBuyerHostname(merchantHost);
  return `https://${host}`;
}

/**
 * https:// merchant (seller) origin for links from the buyer app.
 */
export function merchantOriginFromBuyerHostname(buyerHost: string): string {
  const host = buyerHostnameToMerchantHostname(buyerHost);
  return `https://${host}`;
}

/**
 * Parent domain for shared auth cookies (leading dot), e.g. .vaiu.app, .paywithvaiu.com
 * Strips a leading "sell." or "app." once so app.* and sell.* share the same registrable parent.
 */
export function sharedAuthCookieParentDomain(hostname: string): string | null {
  if (typeof hostname !== 'string') return null;
  const h = hostname.toLowerCase().trim();
  if (h === 'localhost' || h === '127.0.0.1') return null;
  const parts = h.split('.').filter(Boolean);
  if (parts.length < 2) return null;
  if (parts[0] === 'sell' || parts[0] === 'app') {
    return `.${parts.slice(1).join('.')}`;
  }
  return `.${h}`;
}
